import { MenuPlacement } from 'react-select';
import {
  AsyncMultiselectProps,
  MultiselectProps,
  SelectOption,
  SelectOptionsOrGroups,
} from '../types';
import {
  GenericAsyncCreatableSelect,
  GenericAsyncSelect,
  GenericCreatableSelect,
  GenericSelect,
} from './GenericSelect';
import { SelectAllFooter } from './menuFooters';
import { arrayWithItems, findOptions } from './utils';
import { ValueContainerMultiSelectCheckbox } from './reactSelectComponents/ValueContainerMultiSelectCheckbox';
import { ValueContainerWithTruncation } from './reactSelectComponents/ValueContainerWithTruncation';

const MULTISELECT_DEFAULT_PROPS = {
  isMulti: true,
  closeMenuOnSelect: false,
  hideSelectedOptions: false,
  tabSelectsValue: false,
};

const composeProps = <TValue,>(
  {
    value,
    options,
    defaultValue,
    onChange,
    onValueChange,
    menuPlacement,
    minMenuHeight,
    shouldTruncateSelectedOptions,
  }: MultiselectProps<TValue>,
  isCreatable = false
) => ({
  value: Array.isArray(value)
    ? findOptions(value, options, isCreatable)
    : undefined,
  defaultValue: arrayWithItems(findOptions(defaultValue, options, isCreatable)),
  menuPlacement: menuPlacement ?? ('auto' as MenuPlacement),
  minMenuHeight: minMenuHeight ?? 300,
  onChange: (options: SelectOption<TValue>[]) => {
    onChange?.(options);
    onValueChange?.(options.map(option => option.value));
  },
  options: options,
  ...(shouldTruncateSelectedOptions
    ? { components: { ValueContainer: ValueContainerWithTruncation } }
    : {}),
});

export const Multiselect = <TValue,>(props: MultiselectProps<TValue>) => (
  // @ts-expect-error - Type incompatibility with isMulti prop
  <GenericSelect
    {...MULTISELECT_DEFAULT_PROPS}
    {...props}
    {...composeProps(props)}
    footerContent={
      props.showSelectAllFooter && props.options?.length ? (
        <SelectAllFooter
          i18nLocale={props.i18nLocale}
          optionsCount={props.options.length || 0}
          onSelectAll={() => {
            if (!props.options) return;

            const options = (
              props.options as SelectOptionsOrGroups<TValue>
            ).flatMap(option => {
              if ('options' in option) {
                return option.options;
              }
              return option;
            });
            const values = options.map(({ value }) => value);
            props.onChange?.(options);
            props.onValueChange?.(values);
          }}
        />
      ) : null
    }
  />
);

export const CreatableMultiselect = <TValue,>(
  props: Omit<MultiselectProps<TValue>, 'showSelectAllFooter'>
) => (
  // @ts-expect-error I don't know how to fix it yet.
  <GenericCreatableSelect isMulti {...props} {...composeProps(props, true)} />
);

export const AsyncMultiselect = <TValue,>(
  props: AsyncMultiselectProps<TValue>
) => (
  // @ts-expect-error TODO
  <GenericAsyncSelect
    {...MULTISELECT_DEFAULT_PROPS}
    {...props}
    {...composeProps(props)}
  />
);

export const AsyncCreatableMultiselect = <TValue,>(
  props: AsyncMultiselectProps<TValue>
) => (
  // @ts-expect-error TODO
  <GenericAsyncCreatableSelect
    {...MULTISELECT_DEFAULT_PROPS}
    {...props}
    {...composeProps(props, true)}
  />
);

export const MultiSelectCheckbox = <TValue,>(
  props: Omit<MultiselectProps<TValue>, 'placeholder'>
) => (
  <Multiselect
    {...props}
    components={{
      ValueContainer: ValueContainerMultiSelectCheckbox,
      // Rendering empty divs to hide the default implementation of these components
      MultiValue: EmptyDiv,
      MultiValueContainer: EmptyDiv,
      MultiValueRemove: EmptyDiv,
      MultiValueLabel: EmptyDiv,
    }}
    placeholder=""
  />
);

const EmptyDiv = () => <div />;
